// NIS2 incident reporting — Directive (EU) 2022/2555, Art. 23 and
// Commission Implementing Regulation (EU) 2024/2690. Mirrors
// socmate-be/app/services/nis2_service.py.

export type Nis2Stage = 'early_warning' | 'notification' | 'intermediate' | 'final';
export type Nis2StageStatus = 'draft' | 'submitted';

export const NIS2_STAGES: Nis2Stage[] = ['early_warning', 'notification', 'intermediate', 'final'];

export type Nis2Severity = 'low' | 'medium' | 'high' | 'critical';

export type Nis2ThreatCategory =
  | 'ransomware' | 'malware' | 'exploit' | 'denial_of_service' | 'supply_chain'
  | 'phishing' | 'unauthorized_access' | 'data_exfiltration' | 'insider' | 'misconfiguration'
  | 'physical' | 'other';

export interface Nis2Ioc {
  type: string;
  value: string;
}

// ─── Incident-level assessment (stored on the ticket) ───────────────────────

export interface Nis2EntityProfile {
  entity_name: string;
  registration_id: string;          // national NIS2 registration identifier
  entity_type: 'essential' | 'important' | '';
  sector: string;
  member_state: string;             // Member State of main establishment (ISO 3166 alpha-2)
  authority: string;                // designated CSIRT / competent authority
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  cir_applicable: boolean;          // entity is a digital provider covered by CIR 2024/2690
  trust_service_provider: boolean;  // Art. 23(4) 2nd subpara: notification due in 24h, not 72h
}

export interface Nis2Significance {
  // Art. 23(3) — has caused or is capable of causing:
  severe_disruption_or_loss: boolean;       // (a) severe operational disruption or financial loss
  considerable_damage_to_others: boolean;   // (b) considerable material/non-material damage to others
  // CIR 2024/2690 Art. 3(1) general criteria
  financial_loss_eur: number | null;
  annual_turnover_eur: number | null;       // total, preceding financial year
  trade_secret_exfiltration: boolean;
  death: boolean;
  considerable_health_damage: boolean;
  malicious_unauthorised_access: boolean;   // Art. 3(1)(e), capable of severe operational disruption
  // CIR 2024/2690 Art. 4 — recurring incidents
  recurring: boolean;
  // CIR Arts. 5–14 (per entity type) or national criteria
  entity_specific_threshold_met: boolean;
  entity_specific_notes: string;
  // Informational impact figures (thresholds are entity-type specific)
  affected_users: number | null;
  duration_hours: number | null;
  // Analyst decision — SOCmate never auto-qualifies
  decision: 'significant' | 'not_significant' | 'undetermined';
  decision_rationale: string;
}

export interface Nis2Assessment {
  entity?: Partial<Nis2EntityProfile>;
  significance?: Partial<Nis2Significance>;
}

// ─── Stage payloads ──────────────────────────────────────────────────────────

export interface Nis2EarlyWarningData {
  detection_datetime: string;
  incident_type: string;
  affected_services: string;
  initial_summary: string;
  is_malicious_or_unlawful: boolean;
  suspected_cross_border_impact: boolean;
  affected_member_states: string[];
  containment_initiated: boolean;
  measures_taken: string;
  assistance_requested: boolean;
  assistance_details: string;
}

export interface Nis2NotificationData {
  initial_assessment: string;
  severity: Nis2Severity | '';
  threat_category: Nis2ThreatCategory | '';
  affected_users_count: number | null;
  downtime_minutes: number | null;
  estimated_financial_loss_eur: number | null;
  is_malicious_or_unlawful: boolean;
  suspected_cross_border_impact: boolean;
  affected_member_states: string[];
  indicators_of_compromise: Nis2Ioc[];
  gdpr_breach_triggered: boolean;
  measures_taken: string;
}

export interface Nis2IntermediateData {
  requested_at: string;
  request_reference: string;
  current_status: 'ongoing' | 'contained' | 'resolved' | '';
  status_update: string;
}

export interface Nis2FinalData {
  is_incident_resolved: boolean;
  handling_concluded_at: string;
  detailed_description: string;
  severity: Nis2Severity | '';
  impact_summary: string;
  root_cause_analysis: string;
  mitre_attack_techniques: string[];
  mitigations_implemented: string;
  mitigations_ongoing: string;
  cross_border_impact: string;
  lessons_learned: string;
}

export interface Nis2StageDataMap {
  early_warning: Nis2EarlyWarningData;
  notification: Nis2NotificationData;
  intermediate: Nis2IntermediateData;
  final: Nis2FinalData;
}

// ─── API payloads ────────────────────────────────────────────────────────────

export interface Nis2StageReport {
  id: string;
  ticket_id: string;
  stage: Nis2Stage;
  status: Nis2StageStatus;
  data: Record<string, any>;
  created_by: { id: string; full_name?: string; username?: string; email?: string } | null;
  submitted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Nis2Deadline {
  due_at: string | null;
  basis: string;
  completed: boolean;
}

export interface Nis2Report {
  ticket_id: string;
  awareness_at: string | null;
  assessment: Nis2Assessment;
  notifications: Nis2StageReport[];
  deadlines: Record<Nis2Stage, Nis2Deadline>;
  ticket_context: {
    client_name: string | null;
    root_cause: string | null;
    resolved_at: string | null;
  };
}
